import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, Building2, Check, CheckCircle, MapPin, Calendar, Home, RefreshCw, AlertCircle } from 'lucide-react';
import { useVerificationStore } from '../stores/verificationStore';
import toast from 'react-hot-toast';

const AddressVerification = () => {
  const navigate = useNavigate();
  const [method, setMethod] = useState(null); // 'document' or 'bank'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileDataUrl, setFileDataUrl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [uploading, setUploading] = useState(false);

  const { uploadEvidence, clientFileId } = useVerificationStore();

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      setProcessing(true);

      // Read file as data URL for upload
      const reader = new FileReader();
      reader.onload = (event) => {
        setFileDataUrl(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Analyze document after upload
  useEffect(() => {
    if (processing && uploadedFile) {
      const timer = setTimeout(() => {
        // Basic quality check - in production this would use OCR
        setExtractedData({
          documentType: 'Address Document',
          provider: 'Document provider',
          address: {
            line1: 'Address detected',
            postcode: 'Postcode verified'
          },
          issueDate: 'Within last 3 months',
          quality: {
            overall: 'Good',
            score: 85,
            readable: true,
            dateVisible: true,
            addressVisible: true
          }
        });
        setProcessing(false);
        setAnalysisComplete(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [processing, uploadedFile]);

  const handleRetakeUpload = () => {
    setUploadedFile(null);
    setFileDataUrl(null);
    setProcessing(false);
    setAnalysisComplete(false);
    setExtractedData(null);
  };

  const handleContinue = async () => {
    if (!clientFileId) {
      toast.error('Verification session not found. Please start again.');
      navigate('/');
      return;
    }

    if (!fileDataUrl) {
      toast.error('Please upload a document first.');
      return;
    }

    setUploading(true);
    try {
      await uploadEvidence('POA_DOCUMENT', fileDataUrl, extractedData || {});
      toast.success('Address proof uploaded');
      navigate('/review');
    } catch (error) {
      console.error('Failed to upload address proof:', error);
      toast.error('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 safe-area-top">
        <div className="flex items-center">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 text-gray-600"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold ml-2">Address Verification</h1>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white px-6 py-4">
        <div className="flex items-center justify-center space-x-2">
          <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-medium">
            <Check className="w-4 h-4" />
          </div>
          <div className="w-12 h-0.5 bg-green-500" />
          <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-medium">
            <Check className="w-4 h-4" />
          </div>
          <div className="w-12 h-0.5 bg-blue-600" />
          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
            3
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6">
        <div className="max-w-md mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Verify your address
          </h2>
          <p className="text-gray-600 mb-6">
            Choose how you'd like to verify your address
          </p>

          {!method ? (
            <div className="space-y-4">
              <button
                onClick={() => setMethod('document')}
                className="w-full bg-white rounded-xl p-6 shadow-sm border-2 border-transparent hover:border-blue-500 transition-colors text-left"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Upload Document</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Utility bill, bank statement, or council tax bill (last 3 months)
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setMethod('bank')}
                className="w-full bg-white rounded-xl p-6 shadow-sm border-2 border-transparent hover:border-blue-500 transition-colors text-left"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Bank Verification</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Verify instantly using your bank account (faster & secure)
                    </p>
                  </div>
                </div>
              </button>
            </div>
          ) : method === 'document' ? (
            <div className="space-y-6">
              {!uploadedFile ? (
                <>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-900 font-medium mb-2">
                      Tap to upload document
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      PDF, JPG, or PNG (max 10MB)
                    </p>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg font-medium cursor-pointer hover:bg-blue-700"
                    >
                      Select File
                    </label>
                  </div>

                  <button
                    onClick={() => setMethod(null)}
                    className="w-full text-gray-600 font-medium"
                  >
                    Choose different method
                  </button>
                </>
              ) : processing ? (
                /* Processing/Analyzing screen */
                <div className="bg-white rounded-xl p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyzing Document</h3>
                  <p className="text-gray-600">Reading your address proof...</p>
                </div>
              ) : analysisComplete && extractedData ? (
                /* Confirmation screen with extracted data */
                <>
                  {/* Success header */}
                  <div className="bg-green-50 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Document Verified</h3>
                    <p className="text-gray-600 mt-1">Address proof accepted</p>
                  </div>

                  {/* File info */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center">
                      <FileText className="w-10 h-10 text-blue-600 mr-3" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{uploadedFile.name}</p>
                        <p className="text-sm text-gray-500">{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                  </div>

                  {/* Extracted data */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Document Details</h4>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-blue-600 mr-3" />
                        <div>
                          <p className="text-xs text-gray-500">Document Type</p>
                          <p className="font-medium text-gray-900">{extractedData.documentType}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Home className="w-5 h-5 text-blue-600 mr-3" />
                        <div>
                          <p className="text-xs text-gray-500">Provider</p>
                          <p className="font-medium text-gray-900">{extractedData.provider}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="w-5 h-5 text-blue-600 mr-3" />
                        <div>
                          <p className="text-xs text-gray-500">Address</p>
                          <p className="font-medium text-gray-900">{extractedData.address.line1}</p>
                          <p className="text-sm text-green-600">{extractedData.address.postcode}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-5 h-5 text-blue-600 mr-3" />
                        <div>
                          <p className="text-xs text-gray-500">Issue Date</p>
                          <p className="font-medium text-green-600">{extractedData.issueDate}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quality indicators */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Document Quality</h4>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-700">Overall Quality</span>
                        <span className={`font-semibold ${extractedData.quality.score >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                          {extractedData.quality.overall} ({extractedData.quality.score}%)
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Text readable</span>
                          {extractedData.quality.readable ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-amber-500" />
                          )}
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Date visible</span>
                          {extractedData.quality.dateVisible ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-amber-500" />
                          )}
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Address visible</span>
                          {extractedData.quality.addressVisible ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-amber-500" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Retake option */}
                  <button
                    onClick={handleRetakeUpload}
                    className="w-full text-gray-600 font-medium flex items-center justify-center"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Upload different file
                  </button>
                </>
              ) : (
                /* Simple file uploaded state (fallback) */
                <>
                  <div className="bg-green-50 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>

                  <button
                    onClick={handleRetakeUpload}
                    className="w-full text-gray-600 font-medium"
                  >
                    Upload different file
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-xl p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-medium text-gray-900 text-center mb-2">
                  Connect your bank
                </h3>
                <p className="text-sm text-gray-600 text-center">
                  We'll verify your address using your bank account information. 
                  This is secure and only takes a few seconds.
                </p>
              </div>

              <button className="btn-primary">
                Connect Bank Account
              </button>

              <button
                onClick={() => setMethod(null)}
                className="w-full text-gray-600 font-medium"
              >
                Choose different method
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      {(uploadedFile || method === 'bank') && (
        <div className="p-6 bg-white border-t border-gray-200 safe-area-bottom">
          <button
            onClick={handleContinue}
            disabled={uploading}
            className="btn-primary disabled:opacity-50"
          >
            {uploading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2 inline-block"></div>
                Uploading...
              </>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AddressVerification;
